/**
 * FilterBuilder — UND/ODER-Bedingungsbaum mit max. einer Verschachtelungsebene.
 * State wird als base64(JSON) in die URL serialisiert.
 */
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { FilterRequest, FilterCondition } from "@msc/shared";
import { useFilterPreview } from "../../hooks/useCampaigns.js";
import { useCustomers } from "../../hooks/useCustomers.js";

// ── URL-Serialisierung ───────────────────────────────────────────────────────

export function encodeFilter(filter: FilterRequest): string {
  return btoa(unescape(encodeURIComponent(JSON.stringify(filter))));
}

export function decodeFilter(encoded: string): FilterRequest | null {
  try {
    return JSON.parse(decodeURIComponent(escape(atob(encoded)))) as FilterRequest;
  } catch {
    return null;
  }
}

// ── Typen ────────────────────────────────────────────────────────────────────

type ConditionType = FilterCondition["type"];

interface BCGCondition {
  type: "bcg_quadrant";
  operator: "in";
  values: string[];
}

interface ComponentCondition {
  type: "component";
  operator: "any_of";
  values: string[];
}

interface Revenue12mCondition {
  type: "revenue_12m";
  operator: "gte" | "lte";
  value: number;
}

interface SegmentCondition {
  type: "segment";
  operator: "in";
  values: string[];
}

type AnyCondition =
  | BCGCondition
  | ComponentCondition
  | Revenue12mCondition
  | SegmentCondition;

interface FilterGroup {
  logic: "and" | "or";
  conditions: AnyCondition[];
}

interface FilterState {
  logic: "and" | "or";
  groups: FilterGroup[];
}

// ── Hilfsfunktionen ──────────────────────────────────────────────────────────

function emptyCondition(type: ConditionType): AnyCondition {
  switch (type) {
    case "bcg_quadrant":
      return { type: "bcg_quadrant", operator: "in", values: [] };
    case "component":
      return { type: "component", operator: "any_of", values: [] };
    case "revenue_12m":
      return { type: "revenue_12m", operator: "gte", value: 0 };
    case "segment":
      return { type: "segment", operator: "in", values: [] };
  }
}

function isValidCondition(c: AnyCondition): boolean {
  if (c.type === "revenue_12m") return c.value > 0;
  return c.values.length > 0;
}

function stateToFilterRequest(state: FilterState): FilterRequest | null {
  const validGroups = state.groups
    .map((g) => ({ ...g, conditions: g.conditions.filter(isValidCondition) }))
    .filter((g) => g.conditions.length > 0);
  if (validGroups.length === 0) return null;
  return { logic: state.logic, groups: validGroups } as FilterRequest;
}

// ── Konstanten ───────────────────────────────────────────────────────────────

function useBcgQuadrantOptions() {
  const { t } = useTranslation();
  return [
    { value: "star", label: t("quadrant.star") },
    { value: "cash_cow", label: t("quadrant.cash_cow") },
    { value: "question_mark", label: t("quadrant.question_mark") },
    { value: "dog", label: t("quadrant.dog") },
  ];
}

function useConditionTypeOptions(): { value: ConditionType; label: string }[] {
  const { t } = useTranslation();
  return [
    { value: "bcg_quadrant", label: t("pages.filterBuilder.conditionTypeBcg") },
    { value: "component", label: t("pages.filterBuilder.conditionTypeComponent") },
    { value: "revenue_12m", label: t("pages.filterBuilder.conditionTypeRevenue") },
    { value: "segment", label: t("pages.filterBuilder.conditionTypeSegment") },
  ];
}

// ── Props ────────────────────────────────────────────────────────────────────

interface FilterBuilderProps {
  /** Aktueller Filterstate (aus URL oder init) */
  value: FilterState;
  onChange: (state: FilterState) => void;
  /** Optionale Kampagnen-ID für "Bereits Mitglied"-Markierung */
  campaignId?: string;
  /** Callback wenn Kunden ausgewählt werden (mit Namen für UI-Anzeige). */
  onAddCustomers?: (
    customers: Array<{ id: string; name: string }>,
    addType: "filter" | "manual",
  ) => void;
  /** IDs die bereits im Korb sind (für "Bereits hinzugefügt"-Markierung) */
  basketCustomerIds?: Set<string>;
  /** Verfügbare Komponenten für Dropdown */
  availableComponents?: Array<{ id: string; name: string }>;
  /** Verfügbare Segmente für Dropdown */
  availableSegments?: Array<{ id: string; name: string }>;
  /** Filter-Vorschau anzeigen? */
  showPreview?: boolean;
}

// ── Bedingungseditor ─────────────────────────────────────────────────────────

interface ConditionEditorProps {
  condition: AnyCondition;
  onChange: (c: AnyCondition) => void;
  onRemove: () => void;
  availableComponents: Array<{ id: string; name: string }>;
  availableSegments: Array<{ id: string; name: string }>;
}

function ConditionEditor({
  condition,
  onChange,
  onRemove,
  availableComponents,
  availableSegments,
}: ConditionEditorProps) {
  const { t } = useTranslation();
  const bcgQuadrantOptions = useBcgQuadrantOptions();
  const conditionTypeOptions = useConditionTypeOptions();

  function handleTypeChange(type: ConditionType | null) {
    if (type) onChange(emptyCondition(type));
  }

  return (
    <Paper p="sm" withBorder radius="sm">
      <Group align="flex-start" wrap="nowrap">
        <Stack gap="xs" style={{ flex: 1 }}>
          <Select
            label={t("pages.filterBuilder.conditionType")}
            data={conditionTypeOptions}
            value={condition.type}
            onChange={(v) => handleTypeChange(v as ConditionType | null)}
            size="xs"
            style={{ minWidth: 180 }}
          />

          {condition.type === "bcg_quadrant" && (
            <Select
              label={t("pages.filterBuilder.quadrantsLabel")}
              data={bcgQuadrantOptions}
              value={condition.values[0] ?? null}
              onChange={(v) =>
                onChange({ ...condition, values: v ? [v] : [] } as BCGCondition)
              }
              size="xs"
              placeholder={t("pages.filterBuilder.quadrantPlaceholder")}
              clearable
            />
          )}

          {condition.type === "component" && (
            <Select
              label={t("pages.filterBuilder.componentLabel")}
              data={availableComponents.map((c) => ({ value: c.id, label: c.name }))}
              value={condition.values[0] ?? null}
              onChange={(v) =>
                onChange({ ...condition, values: v ? [v] : [] } as ComponentCondition)
              }
              size="xs"
              placeholder={t("pages.filterBuilder.componentPlaceholder")}
              clearable
              searchable
            />
          )}

          {condition.type === "revenue_12m" && (
            <Group gap="xs" align="flex-end">
              <Select
                label={t("pages.filterBuilder.operatorLabel")}
                data={[
                  { value: "gte", label: t("pages.filterBuilder.operatorGte") },
                  { value: "lte", label: t("pages.filterBuilder.operatorLte") },
                ]}
                value={condition.operator}
                onChange={(v) =>
                  onChange({
                    ...condition,
                    operator: (v ?? "gte") as "gte" | "lte",
                  } as Revenue12mCondition)
                }
                size="xs"
                style={{ minWidth: 150 }}
              />
              <NumberInput
                label={t("pages.filterBuilder.amountLabel")}
                value={condition.value}
                onChange={(v) =>
                  onChange({ ...condition, value: Number(v) || 0 } as Revenue12mCondition)
                }
                min={0}
                step={1000}
                size="xs"
                style={{ minWidth: 140 }}
              />
            </Group>
          )}

          {condition.type === "segment" && (
            <Select
              label={t("pages.filterBuilder.segmentLabel")}
              data={availableSegments.map((s) => ({ value: s.id, label: s.name }))}
              value={condition.values[0] ?? null}
              onChange={(v) =>
                onChange({ ...condition, values: v ? [v] : [] } as SegmentCondition)
              }
              size="xs"
              placeholder={t("pages.filterBuilder.segmentPlaceholder")}
              clearable
            />
          )}
        </Stack>

        <ActionIcon
          color="red"
          variant="subtle"
          size="sm"
          mt={22}
          onClick={onRemove}
          title={t("pages.filterBuilder.conditionRemoveTitle")}
        >
          ✕
        </ActionIcon>
      </Group>
    </Paper>
  );
}

// ── Gruppeneditor ────────────────────────────────────────────────────────────

interface GroupEditorProps {
  group: FilterGroup;
  groupIndex: number;
  onChange: (g: FilterGroup) => void;
  onRemove: () => void;
  availableComponents: Array<{ id: string; name: string }>;
  availableSegments: Array<{ id: string; name: string }>;
}

function GroupEditor({
  group,
  groupIndex,
  onChange,
  onRemove,
  availableComponents,
  availableSegments,
}: GroupEditorProps) {
  const { t } = useTranslation();
  function addCondition() {
    onChange({
      ...group,
      conditions: [...group.conditions, emptyCondition("bcg_quadrant")],
    });
  }

  function updateCondition(i: number, c: AnyCondition) {
    const conditions = [...group.conditions];
    conditions[i] = c;
    onChange({ ...group, conditions });
  }

  function removeCondition(i: number) {
    const conditions = group.conditions.filter((_, idx) => idx !== i);
    onChange({ ...group, conditions });
  }

  return (
    <Box
      p="sm"
      style={(theme) => ({
        border: `1px solid ${theme.colors.blue[3]}`,
        borderRadius: theme.radius.sm,
        background: theme.colors.blue[0],
      })}
    >
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <Text size="xs" fw={600} c="blue">
            {t("pages.filterBuilder.groupName", { n: groupIndex + 1 })}
          </Text>
          <Select
            data={[
              { value: "and", label: t("pages.filterBuilder.groupLogicAnd") },
              { value: "or", label: t("pages.filterBuilder.groupLogicOr") },
            ]}
            value={group.logic}
            onChange={(v) => onChange({ ...group, logic: (v ?? "and") as "and" | "or" })}
            size="xs"
            style={{ minWidth: 200 }}
          />
        </Group>
        <ActionIcon
          color="red"
          variant="subtle"
          size="sm"
          onClick={onRemove}
          title={t("pages.filterBuilder.groupRemoveTitle")}
        >
          ✕
        </ActionIcon>
      </Group>

      <Stack gap="xs">
        {group.conditions.map((c, i) => (
          <ConditionEditor
            key={i}
            condition={c}
            onChange={(updated) => updateCondition(i, updated)}
            onRemove={() => removeCondition(i)}
            availableComponents={availableComponents}
            availableSegments={availableSegments}
          />
        ))}
      </Stack>

      <Button
        size="xs"
        variant="light"
        mt="xs"
        onClick={addCondition}
      >
        {t("pages.filterBuilder.groupAddCondition")}
      </Button>
    </Box>
  );
}

// ── FilterBuilder (Hauptkomponente) ──────────────────────────────────────────

export function FilterBuilder({
  value,
  onChange,
  campaignId,
  onAddCustomers,
  basketCustomerIds = new Set(),
  availableComponents = [],
  availableSegments = [],
  showPreview = true,
}: FilterBuilderProps) {
  const { t } = useTranslation();
  const [manualSearch, setManualSearch] = useState("");

  const filterRequest = stateToFilterRequest(value);
  const { data: previewData, isFetching: previewLoading } = useFilterPreview(
    showPreview ? filterRequest : null,
    campaignId,
  );

  function addGroup() {
    onChange({
      ...value,
      groups: [
        ...value.groups,
        { logic: "and", conditions: [emptyCondition("bcg_quadrant")] },
      ],
    });
  }

  function updateGroup(i: number, g: FilterGroup) {
    const groups = [...value.groups];
    groups[i] = g;
    onChange({ ...value, groups });
  }

  function removeGroup(i: number) {
    const groups = value.groups.filter((_, idx) => idx !== i);
    onChange({ ...value, groups });
  }

  function handleAddAll() {
    if (!previewData || !onAddCustomers) return;
    const picks = previewData.customers
      .filter((c) => !basketCustomerIds.has(c.id))
      .map((c) => ({ id: c.id, name: c.name }));
    if (picks.length > 0) onAddCustomers(picks, "filter");
  }

  function handleAddSingle(customerId: string) {
    if (!onAddCustomers || !previewData) return;
    const found = previewData.customers.find((c) => c.id === customerId);
    if (!found) return;
    onAddCustomers([{ id: found.id, name: found.name }], "filter");
  }

  return (
    <Stack gap="md">
      {/* Äußerer Connector */}
      <Group gap="xs" align="center">
        <Text size="sm" fw={500}>
          {t("pages.filterBuilder.groupsLinkWith")}
        </Text>
        <Select
          data={[
            { value: "and", label: t("common.and") },
            { value: "or", label: t("common.or") },
          ]}
          value={value.logic}
          onChange={(v) => onChange({ ...value, logic: (v ?? "and") as "and" | "or" })}
          size="xs"
          style={{ width: 110 }}
        />
      </Group>

      {/* Gruppen */}
      <Stack gap="sm">
        {value.groups.map((g, i) => (
          <GroupEditor
            key={i}
            group={g}
            groupIndex={i}
            onChange={(updated) => updateGroup(i, updated)}
            onRemove={() => removeGroup(i)}
            availableComponents={availableComponents}
            availableSegments={availableSegments}
          />
        ))}
      </Stack>

      <Button
        variant="light"
        size="sm"
        onClick={addGroup}
        style={{ alignSelf: "flex-start" }}
      >
        {t("pages.filterBuilder.addGroup")}
      </Button>

      {/* Filter-Vorschau */}
      {showPreview && (
        <>
          <Divider label={t("pages.filterBuilder.previewLabel")} labelPosition="center" />

          {previewLoading && (
            <Text size="sm" c="dimmed">
              {t("pages.filterBuilder.previewLoading")}
            </Text>
          )}

          {!previewLoading && previewData && (
            <Stack gap="sm">
              <Group justify="space-between">
                <Badge size="lg" color="blue">
                  {t("pages.filterBuilder.previewMatchCount", { count: previewData.count })}
                </Badge>
                {onAddCustomers && previewData.customers.length > 0 && (
                  <Button size="xs" onClick={handleAddAll}>
                    {t("pages.filterBuilder.previewTakeAll", {
                      count: previewData.customers.filter((c) => !basketCustomerIds.has(c.id)).length,
                    })}
                  </Button>
                )}
              </Group>

              {previewData.customers.map((c) => (
                <Paper key={c.id} p="xs" withBorder>
                  <Group justify="space-between">
                    <Stack gap={2}>
                      <Text size="sm" fw={500}>
                        {c.name}
                      </Text>
                      <Group gap="xs">
                        {c.quadrant && (
                          <Badge size="xs" color="gray" variant="light">
                            {c.quadrant}
                          </Badge>
                        )}
                        {c.revenue12m > 0 && (
                          <Text size="xs" c="dimmed">
                            {new Intl.NumberFormat("de-DE", {
                              style: "currency",
                              currency: "EUR",
                              maximumFractionDigits: 0,
                            }).format(c.revenue12m)}
                          </Text>
                        )}
                        {c.alreadyInCampaign && (
                          <Badge size="xs" color="green" variant="light">
                            {t("pages.filterBuilder.alreadyMember")}
                          </Badge>
                        )}
                        {basketCustomerIds.has(c.id) && !c.alreadyInCampaign && (
                          <Badge size="xs" color="teal" variant="light">
                            {t("pages.filterBuilder.alreadySelected")}
                          </Badge>
                        )}
                      </Group>
                    </Stack>
                    {onAddCustomers && !basketCustomerIds.has(c.id) && !c.alreadyInCampaign && (
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => handleAddSingle(c.id)}
                      >
                        {t("pages.filterBuilder.takeOver")}
                      </Button>
                    )}
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}

          {!previewLoading && !previewData && filterRequest && (
            <Text size="sm" c="dimmed">
              {t("pages.filterBuilder.previewBuilding")}
            </Text>
          )}
        </>
      )}

      {/* Manuelle Suche */}
      {onAddCustomers && (
        <ManualSearch
          basketCustomerIds={basketCustomerIds}
          onAdd={(c) => onAddCustomers([c], "manual")}
          value={manualSearch}
          onChange={setManualSearch}
        />
      )}
    </Stack>
  );
}

// ── Manuelle Kunden-Suche ─────────────────────────────────────────────────────

interface ManualSearchProps {
  basketCustomerIds: Set<string>;
  onAdd: (customer: { id: string; name: string }) => void;
  value: string;
  onChange: (v: string) => void;
}

function ManualSearch({ basketCustomerIds, onAdd, value, onChange }: ManualSearchProps) {
  const { t } = useTranslation();
  const [debouncedQuery] = useDebouncedValue(value, 300);
  const enabled = debouncedQuery.trim().length >= 2;
  const { data, isFetching } = useCustomers({
    q: enabled ? debouncedQuery.trim() : undefined,
    page: 1,
  });

  const results = (data?.items ?? []).filter((c) => !basketCustomerIds.has(c.id));

  return (
    <Stack gap="xs">
      <Divider label={t("pages.filterBuilder.manualSearchDivider")} labelPosition="center" />
      <TextInput
        placeholder={t("pages.filterBuilder.manualSearchPlaceholder")}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        size="sm"
        description={t("pages.filterBuilder.manualSearchDescription")}
      />

      {enabled && (
        <Stack gap={4}>
          {isFetching && (
            <Text size="xs" c="dimmed">
              {t("pages.filterBuilder.manualSearchSearching")}
            </Text>
          )}
          {!isFetching && results.length === 0 && (
            <Text size="xs" c="dimmed">
              {t("pages.filterBuilder.manualSearchNoMore")}
            </Text>
          )}
          {results.slice(0, 10).map((c) => (
            <Paper key={c.id} p="xs" withBorder>
              <Group justify="space-between" wrap="nowrap">
                <Text size="sm">{c.name}</Text>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => {
                    onAdd({ id: c.id, name: c.name });
                  }}
                >
                  {t("pages.filterBuilder.manualSearchAdd")}
                </Button>
              </Group>
            </Paper>
          ))}
          {results.length > 10 && (
            <Text size="xs" c="dimmed">
              {t("pages.filterBuilder.manualSearchTooManyHidden", { count: results.length - 10 })}
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
}

// ── Default Empty State ──────────────────────────────────────────────────────

export function emptyFilterState(): FilterState {
  return {
    logic: "and",
    groups: [],
  };
}

export type { FilterState, FilterGroup, AnyCondition };
